// spanishLaneV4Config.ts
// ─── Astria Spanish V2 — "Spanish Lane v4 / Premium Multi-Country System" ──
// Per client spec (fixes.txt): one universal premium UI frame whose accent
// color, section-block labels, and micro-action footer change per country,
// while tone/content stays with the existing 7-tone LLM engine in
// helper/astriaSpanishService.js (SPANISH_LANE_V4_CONFIG — keep both in
// sync). This file is presentation metadata only; it does not affect what
// the AI says, only how the reply is styled.

export type SpanishTone =
  | "neutral"
  | "spain"
  | "mexico"
  | "argentina"
  | "colombia"
  | "chile"
  | "peru"
  | "astro_deep";

export interface SpanishLaneV4Country {
  key: SpanishTone;
  name: string;
  flagLabel: string;
  accentColor: string;
  accentSoft: string;
  uiStyle:
    | "card_stack_ribbon"
    | "editorial_minimal"
    | "bubble_conversational"
    | "warm_minimal"
    | "soft_analytical"
    | "gentle_ribbon"
    | "clean_minimal"
    | "technical_minimal";
  sections: string[];
  microActionLabel: string;
}

// Client JSON: spanish_lane_v4.universal_frame
export const SPANISH_LANE_V4_FRAME = {
  headerHeightPx: 64,
  cardRadiusPx: 18,
  cardPaddingPx: 24,
  lineHeight: 1.55,
  paragraphSpacingPx: 14,
  dividerColor: "#E6E6EA",
  headerFont: "'Georgia', 'Times New Roman', serif",
  bodyFont: "'Inter', sans-serif",
};

export const SPANISH_LANE_V4_COUNTRIES: Record<
  SpanishTone,
  SpanishLaneV4Country
> = {
  mexico: {
    key: "mexico",
    name: "México",
    flagLabel: "México",
    accentColor: "#D96F52",
    accentSoft: "rgba(217,111,82,0.14)",
    uiStyle: "card_stack_ribbon",
    sections: [
      "APERTURA",
      "ENERGÍA ACTUAL",
      "FORTALEZAS / PATRONES",
      "FRICCIONES / DESAFÍOS",
    ],
    microActionLabel: "Acción para hoy",
  },
  spain: {
    key: "spain",
    name: "España",
    flagLabel: "España",
    accentColor: "#C9A44A",
    accentSoft: "rgba(201,164,74,0.14)",
    uiStyle: "editorial_minimal",
    sections: ["APERTURA", "EQUILIBRIO", "NOTA"],
    microActionLabel: "Reflexión breve",
  },
  argentina: {
    key: "argentina",
    name: "Argentina",
    flagLabel: "Argentina",
    accentColor: "#3A7DFF",
    accentSoft: "rgba(58,125,255,0.14)",
    uiStyle: "bubble_conversational",
    sections: ["APERTURA", "CHARLA", "PUNTO CLAVE"],
    microActionLabel: "Charla rápida",
  },
  colombia: {
    key: "colombia",
    name: "Colombia",
    flagLabel: "Colombia",
    accentColor: "#E8C84A",
    accentSoft: "rgba(232,200,74,0.16)",
    uiStyle: "warm_minimal",
    sections: ["APERTURA", "ENERGÍA", "CAMINO"],
    microActionLabel: "Consejito cálido",
  },
  chile: {
    key: "chile",
    name: "Chile",
    flagLabel: "Chile",
    accentColor: "#9AA3A8",
    accentSoft: "rgba(154,163,168,0.16)",
    uiStyle: "soft_analytical",
    sections: ["APERTURA", "ANÁLISIS", "CLARIDAD"],
    microActionLabel: "Punto claro",
  },
  peru: {
    key: "peru",
    name: "Perú",
    flagLabel: "Perú",
    accentColor: "#7A2F3F",
    accentSoft: "rgba(122,47,63,0.14)",
    uiStyle: "gentle_ribbon",
    sections: ["APERTURA", "EQUILIBRIO", "NOTA"],
    microActionLabel: "Nota suave",
  },
  neutral: {
    key: "neutral",
    name: "Neutral",
    flagLabel: "Universal",
    accentColor: "#6D6F7A",
    accentSoft: "rgba(109,111,122,0.14)",
    uiStyle: "clean_minimal",
    sections: ["APERTURA", "ENERGÍA", "NOTA"],
    microActionLabel: "Acción simple",
  },
  astro_deep: {
    key: "astro_deep",
    name: "Astro-Deep",
    flagLabel: "Astro-Deep",
    accentColor: "#4A4A57",
    accentSoft: "rgba(74,74,87,0.14)",
    uiStyle: "technical_minimal",
    sections: ["APERTURA", "CONFIGURACIÓN", "TENSIÓN", "INTEGRACIÓN"],
    microActionLabel: "Micro-insight",
  },
};

export function getSpanishLaneV4Country(
  tone: SpanishTone | string | null | undefined,
): SpanishLaneV4Country {
  return (
    SPANISH_LANE_V4_COUNTRIES[(tone as SpanishTone) || "neutral"] ||
    SPANISH_LANE_V4_COUNTRIES.neutral
  );
}

export const SPANISH_LANE_V4_ORDER: SpanishTone[] = [
  "neutral",
  "spain",
  "mexico",
  "argentina",
  "colombia",
  "chile",
  "peru",
];
